"use client";

import { useEffect, useRef, useState } from "react";
import type { MapClient, MapProps, MarkerProps } from "../../../types";
import { type Map as MapboxMap, Marker, Popup } from "@maptiler/sdk";

export function MarkerComponent<T>({
  item,
  index,
  map,
  parentProps,
}: Omit<MarkerProps<T>, "isActive" | "markerElementRef"> & {
  map: MapClient<MapboxMap> | null;
  parentProps: MapProps<T, MapboxMap>;
}) {
  const markerRef = useRef<Marker | null>(null);
  const markerElement = useRef<HTMLDivElement | null>(null);
  const popupElement = useRef<HTMLDivElement | null>(null);

  const [active, setActive] = useState(false);

  const coords = parentProps.coordsGetter(item);

  // Create marker and popup once on mount — same rationale as the mapbox
  // provider: prevents popup destruction when supercluster recalculates.
  useEffect(() => {
    if (!map || !markerElement.current) return;

    const marker = new Marker({
      element: markerElement.current,
    })
      .setLngLat({ lat: coords.lat, lng: coords.lng })
      .addTo(map.getRawClient());

    markerRef.current = marker;

    if (parentProps.renderPopup && popupElement.current) {
      const popup = new Popup({
        closeButton: parentProps.settings?.popup?.closeButton,
        closeOnClick: parentProps.settings?.popup?.closeOnClick,
        closeOnMove: parentProps.settings?.popup?.closeOnMove,
        maxWidth: parentProps.settings?.popup?.maxWidth ?? "300px",
        offset: parentProps.settings?.popup?.offset ?? 14,
      }).setDOMContent(popupElement.current);

      popup.on("open", () => setActive(true));
      popup.on("close", () => setActive(false));

      marker.setPopup(popup);
    }

    return () => {
      marker.remove();
    };
  }, [map]);

  // Update position when coordinates change without recreating the marker.
  useEffect(() => {
    markerRef.current?.setLngLat({ lat: coords.lat, lng: coords.lng });
  }, [coords.lat, coords.lng]);

  if (!parentProps.renderMarker) {
    return null;
  }

  return (
    <div>
      {parentProps.renderMarker({
        markerElementRef: markerElement,
        item,
        index,
        isActive: active,
      })}

      <div ref={popupElement}>
        {parentProps.renderPopup?.({ item, index, isActive: active })}
      </div>
    </div>
  );
}

MarkerComponent.displayName = "MarkerComponent";
